import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/authService';
import { colors } from '../styles/colors';
import { CompanySubscription as CompanySubscriptionType, CompanyBilling, CompanySubscriptionUsage } from '../types';
import { formatCNPJ, maskPhone, maskCEP } from '../utils/formatters';
import SlotDisplay from './SlotDisplay';

interface CompanySubscriptionProps {
  companyId: number;
  companyName: string;
  onClose: () => void;
}

const CompanySubscription: React.FC<CompanySubscriptionProps> = ({ companyId, companyName, onClose }) => {
  const [subscription, setSubscription] = useState<CompanySubscriptionType | null>(null);
  const [billing, setBilling] = useState<CompanyBilling | null>(null);
  const [usage, setUsage] = useState<CompanySubscriptionUsage | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscriptionData();
  }, [companyId]);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      const [subscriptionRes, companyRes, billingRes, usageRes] = await Promise.all([
        api.get(`/companies/${companyId}/subscription`).catch(() => ({ data: null })),
        api.get(`/companies/${companyId}`).catch(() => ({ data: null })),
        api.get(`/companies/${companyId}/billing`).catch(() => ({ data: null })),
        api.get(`/companies/${companyId}/subscription/usage`).catch(() => ({ data: null }))
      ]);
      
      setSubscription(subscriptionRes.data);
      
      // Try direct billing endpoint first, fallback to company.billing
      const billingData = billingRes.data || companyRes.data?.billing;
      setBilling(billingData || null);
      setUsage(usageRes.data);
    } catch (error: any) {
      console.error('Error fetching subscription data:', error);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  };

  const getCandidatesDatabaseLabel = (role: number) => {
    const roleMap: { [key: number]: string } = {
      0: 'Sem Acesso',
      1: 'Básico', 
      2: 'Acesso Completo'
    };
    return roleMap[role] || 'Desconhecido';
  };

  const getCaesarLabel = (role: number) => {
    const roleMap: { [key: number]: string } = {
      0: 'Padrão',
      1: 'Pro'
    };
    return roleMap[role] || 'Desconhecido';
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getStatusBadge = () => {
    if (!subscription) return null;
    
    if (subscription.active) {
      if (subscription.canceled_at) {
        return <span style={{...styles.badge, backgroundColor: '#FF9800'}}>CANCELAMENTO AGENDADO</span>;
      }
      return <span style={{...styles.badge, backgroundColor: colors.success}}>ATIVO</span>;
    } else if (subscription.deactivated_at) {
      return <span style={{...styles.badge, backgroundColor: colors.error}}>DESATIVADO</span>;
    }
    return <span style={{...styles.badge, backgroundColor: colors.textLight}}>INATIVO</span>;
  };

  if (loading) {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={styles.loading}>Carregando...</div>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={styles.header}>
            <h2 style={styles.title}>Assinatura - {companyName}</h2>
            <button onClick={onClose} style={styles.closeBtn}>×</button>
          </div>
          <div style={styles.content}>
            <div style={styles.emptyState}>
              <h3>Nenhuma assinatura ativa</h3>
              <p>Esta empresa não possui uma assinatura ativa no momento.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Assinatura - {companyName}</h2>
          <button onClick={onClose} style={styles.closeBtn}>×</button>
        </div>
        
        <div style={styles.content}>
          <div style={styles.columns}>
            {/* Coluna Principal */}
            <div style={styles.column}>
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Informações Gerais</h3>
                <div style={styles.infoGrid}>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Status</div>
                    <div style={styles.infoValue}>{getStatusBadge()}</div>
                  </div>

                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Trial</div>
                    <div style={styles.infoValue}>
                      {subscription.trial ? (
                        <span style={{...styles.badge, backgroundColor: colors.success}}>SIM</span>
                      ) : (
                        <span style={{...styles.badge, backgroundColor: colors.textLight}}>NÃO</span>
                      )}
                    </div>
                  </div>

                  {subscription.trial && subscription.trial_ends_at && (
                    <div style={styles.infoItem}>
                      <div style={styles.infoLabel}>Trial até</div>
                      <div style={styles.infoValue}>{formatDate(subscription.trial_ends_at)}</div>
                    </div>
                  )}

                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Ativado em</div>
                    <div style={styles.infoValue}>{formatDate(subscription.activated_at)}</div>
                  </div>

                  {subscription.deactivated_at && (
                    <div style={styles.infoItem}>
                      <div style={styles.infoLabel}>Desativado em</div>
                      <div style={styles.infoValue}>{formatDate(subscription.deactivated_at)}</div>
                    </div>
                  )}

                  {subscription.canceled_at && (
                    <div style={styles.infoItem}>
                      <div style={styles.infoLabel}>Cancelado em</div>
                      <div style={styles.infoValue}>{formatDate(subscription.canceled_at)}</div>
                    </div>
                  )}

                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Dia de fechamento</div>
                    <div style={styles.infoValue}>{subscription.closing_day || '-'}</div>
                  </div>

                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Método de pagamento</div>
                    <div style={styles.infoValue}>{subscription.payment_method || '-'}</div>
                  </div>

                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Pré-pago</div>
                    <div style={styles.infoValue}>
                      {subscription.is_pre_paid ? 'Sim' : 'Não'}
                    </div>
                  </div>

                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Criado em</div>
                    <div style={styles.infoValue}>{formatDate(subscription.created_at)}</div>
                  </div>

                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Atualizado em</div>
                    <div style={styles.infoValue}>{formatDate(subscription.updated_at)}</div>
                  </div>
                </div>
              </div>

              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Recursos e Limites</h3>
                <div style={styles.infoGrid}>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Banco de Talentos</div>
                    <div style={styles.infoValue}>
                      {getCandidatesDatabaseLabel(subscription.candidates_database_role)}
                    </div>
                  </div>

                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Alertas por mês</div>
                    <div style={styles.infoValue}>{subscription.candidates_database_alerts_per_month}</div>
                  </div>

                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Contatos do BD</div>
                    <div style={styles.infoValue}>{subscription.candidates_database_contacts}</div>
                  </div>

                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Dias de exibição</div>
                    <div style={styles.infoValue}>{subscription.display_contacts_listing_days}</div>
                  </div>

                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Caesar</div>
                    <div style={styles.infoValue}>{getCaesarLabel(subscription.caesar_role)}</div>
                  </div>

                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Slots Caesar</div>
                    <div style={styles.infoValue}><SlotDisplay value={subscription.caesar_slots} /></div>
                  </div>

                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Slots de Oportunidades</div>
                    <div style={styles.infoValue}><SlotDisplay value={subscription.opportunities_slots} /></div>
                  </div>

                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Slots de Destaque</div>
                    <div style={styles.infoValue}><SlotDisplay value={subscription.highlight_slots} /></div>
                  </div>

                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Confidenciais</div>
                    <div style={styles.infoValue}><SlotDisplay value={subscription.confidentials} /></div>
                  </div>

                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Slots de Recrutadores</div>
                    <div style={styles.infoValue}><SlotDisplay value={subscription.recruiters_slots} /></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Coluna Lateral */}
            <div style={styles.column}>
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Dados de Cobrança</h3>
                <div style={styles.infoGrid}>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>CNPJ</div>
                    <div style={styles.infoValue}>{billing?.cnpj ? formatCNPJ(billing.cnpj) : '-'}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Nome</div>
                    <div style={styles.infoValue}>{billing?.name || '-'}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Email</div>
                    <div style={styles.infoValue}>{billing?.email || '-'}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Telefone</div>
                    <div style={styles.infoValue}>
                      {billing?.phone ? maskPhone(billing.phone) : '-'}
                    </div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Info</div>
                    <div style={styles.infoValue}>{billing?.info || '-'}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>CEP</div>
                    <div style={styles.infoValue}>{billing?.zip ? maskCEP(billing.zip) : '-'}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Bairro</div>
                    <div style={styles.infoValue}>{billing?.neighborhood || '-'}</div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Endereço</div>
                    <div style={styles.infoValue}>
                      {billing?.address1 ? `${billing.address1}, ${billing.number || 'S/N'}` : '-'}
                    </div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Complemento</div>
                    <div style={styles.infoValue}>
                      {billing?.address2 || '-'}
                    </div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Cidade/Estado</div>
                    <div style={styles.infoValue}>
                      {billing?.city && billing?.state ? `${billing.city}/${billing.state}` : '-'}
                    </div>
                  </div>
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>Simples Nacional</div>
                    <div style={styles.infoValue}>{billing?.simples_nacional ? 'Sim' : 'Não'}</div>
                  </div>
                </div>
              </div>

              {!subscription.trial && (
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Valores</h3>
                  <div style={styles.infoGrid}>
                    <div style={styles.infoItem}>
                      <div style={styles.infoLabel}>MRR</div>
                      <div style={styles.infoValue}>{formatCurrency(subscription.mrr_value)}</div>
                    </div>
                    <div style={styles.infoItem}>
                      <div style={styles.infoLabel}>Mensalidade</div>
                      <div style={styles.infoValue}>
                        {formatCurrency(subscription.bill_value)}
                        {subscription.mrr_value > 0 && (
                          <span style={styles.discount}>
                            {' '}(desconto: {(100 - (subscription.bill_value * 100) / subscription.mrr_value).toFixed(2)}%)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!subscription.trial && (
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Termos de Uso</h3>
                  <div style={styles.infoGrid}>
                    <div style={styles.infoItem}>
                      <div style={styles.infoLabel}>Aceito</div>
                      <div style={styles.infoValue}>
                        {subscription.terms_accepted ? (
                          <span style={{...styles.badge, backgroundColor: colors.success}}>SIM</span>
                        ) : (
                          <span style={{...styles.badge, backgroundColor: colors.textLight}}>NÃO</span>
                        )}
                      </div>
                    </div>
                    {subscription.terms_accepted_at && (
                      <div style={styles.infoItem}>
                        <div style={styles.infoLabel}>Aceito em</div>
                        <div style={styles.infoValue}>{formatDate(subscription.terms_accepted_at)}</div>
                      </div>
                    )}
                    {subscription.terms_attachment_file_name && (
                      <div style={styles.infoItem}>
                        <div style={styles.infoLabel}>Anexo</div>
                        <div style={styles.infoValue}>{subscription.terms_attachment_file_name}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {subscription.trial && (
                <>
                  <div style={styles.section}>
                    <h3 style={styles.sectionTitle}>Welcome Check</h3>
                    <div style={styles.infoGrid}>
                      <div style={styles.infoItem}>
                        <div style={styles.infoLabel}>Checked</div>
                        <div style={styles.infoValue}>
                          {subscription.trial_welcome_checked ? (
                            <span style={{...styles.badge, backgroundColor: colors.success}}>SIM</span>
                          ) : (
                            <span style={{...styles.badge, backgroundColor: colors.textLight}}>NÃO</span>
                          )}
                        </div>
                      </div>
                      {subscription.trial_welcome_checked_at && (
                        <div style={styles.infoItem}>
                          <div style={styles.infoLabel}>Quando</div>
                          <div style={styles.infoValue}>{formatDate(subscription.trial_welcome_checked_at)}</div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={styles.section}>
                    <h3 style={styles.sectionTitle}>Expired Check</h3>
                    <div style={styles.infoGrid}>
                      <div style={styles.infoItem}>
                        <div style={styles.infoLabel}>Checked</div>
                        <div style={styles.infoValue}>
                          {subscription.trial_expired_checked ? (
                            <span style={{...styles.badge, backgroundColor: colors.success}}>SIM</span>
                          ) : (
                            <span style={{...styles.badge, backgroundColor: colors.textLight}}>NÃO</span>
                          )}
                        </div>
                      </div>
                      {subscription.trial_expired_checked_at && (
                        <div style={styles.infoItem}>
                          <div style={styles.infoLabel}>Quando</div>
                          <div style={styles.infoValue}>{formatDate(subscription.trial_expired_checked_at)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {usage && (
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Uso {subscription.trial ? 'do Trial' : 'Atual'}</h3>
                  <div style={styles.infoGrid}>
                    <div style={styles.infoItem}>
                      <div style={styles.infoLabel}>Contatos abertos</div>
                      <div style={styles.infoValue}>{usage.contacts_opened}</div>
                    </div>
                    <div style={styles.infoItem}>
                      <div style={styles.infoLabel}>Alertas enviados</div>
                      <div style={styles.infoValue}>{usage.alerts_sent}</div>
                    </div>
                    <div style={styles.infoItem}>
                      <div style={styles.infoLabel}>Oportunidades publicadas</div>
                      <div style={styles.infoValue}>{usage.opportunities_published}</div>
                    </div>
                    <div style={styles.infoItem}>
                      <div style={styles.infoLabel}>Candidatos</div>
                      <div style={styles.infoValue}>{usage.total_candidates}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    width: '95%',
    maxWidth: '1200px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: `1px solid ${colors.border}`,
  },
  title: {
    margin: 0,
    color: colors.primary,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: colors.textLight,
  },
  content: {
    padding: '1.5rem',
  },
  columns: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '2rem',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },
  column: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1.5rem',
  },
  section: {
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    padding: '1rem',
  },
  sectionTitle: {
    margin: '0 0 1rem 0',
    color: colors.primary,
    fontSize: '1.1rem',
    fontWeight: 'bold',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
  },
  infoLabel: {
    fontSize: '0.85rem',
    fontWeight: 'bold',
    color: colors.textLight,
  },
  infoValue: {
    fontSize: '0.95rem',
    color: colors.text,
  },
  badge: {
    color: colors.white,
    padding: '0.2rem 0.6rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    display: 'inline-block',
  },
  discount: {
    fontSize: '0.8rem',
    color: colors.textLight,
    marginLeft: '0.5rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    fontSize: '1.2rem',
    color: colors.textLight,
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: colors.textLight,
  },
};

export default CompanySubscription;
